<template>
    <ul class="layout-menu">
        <template v-for="(item, index) in model" :key="item.label + '_' + (item.to || item.path || index)">
            <AppMenuItem v-if="!item.separator" :item="item" :index="index" root />

            <li v-else class="menu-separator" />
        </template>
    </ul>
</template>

<script setup>
import { useApiClient } from '@/composables/useApiClient';
import { loadCurrentUser, useCurrentUser } from '@/composables/useCurrentUser';
import { useFeedback } from '@/composables/useFeedback';
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import AppMenuItem from './AppMenuItem.vue';

// The ledgers are seeded, one per currency, with database ids, so the Accounting group cannot name
// them statically: it lists what GET /ledgers returns, one link per ledger straight to its View.
// There is no menu entry for the ledger list or the account list; the accounts are reached from the
// ledger's View.
const ledgers = ref([]);
//resolved during setup: the toast behind it is injected, which an async callback cannot do
const feedback = useFeedback();
const router = useRouter();
const { can } = useCurrentUser();

onMounted(async () => {
    //only a user who may open a ledger gets the Accounting group, so only they need the ledgers
    await loadCurrentUser();
    if (!can('CanEditLedger')) return;
    try {
        //both paging parameters spelled out: the API binds them as int and rejects an empty one
        const page = await useApiClient().ledgers.get({ queryParameters: { pageNumber: 1, pageSize: 50 } });
        ledgers.value = page?.items ?? [];
    } catch (error) {
        feedback.error('Loading the ledgers for the menu failed', error);
    }
});

// An entry shows when the user passes the policy of the route it opens; the route table is the one
// place those policies live. A group left without entries is dropped with its separator.
const isOpen = (item) => can(router.resolve(item.to).meta.policy);

const model = computed(() =>
    groups.value
        .map((group) => ({ ...group, items: group.items.filter(isOpen) }))
        .filter((group) => group.items.length > 0)
        .flatMap((group) => [{ separator: true }, group])
);

const groups = computed(() => [
    {
        label: 'Dashboard',
        icon: 'pi pi-home',
        path: '/dashboard',
        items: [
            {
                label: 'Overview',
                icon: 'pi pi-fw pi-gauge',
                to: '/dashboard/overview'
            }
        ]
    },
    {
        label: 'Customers',
        icon: 'pi pi-home',
        path: '/customers',
        items: [
            {
                label: 'All',
                icon: 'pi pi-fw pi-users',
                to: '/customers/all'
            },
            {
                label: 'Consumer',
                icon: 'pi pi-fw pi-user',
                to: '/customers/consumer'
            },
            {
                label: 'Business',
                icon: 'pi pi-fw pi-building',
                to: '/customers/business'
            }
        ]
    },
    {
        // every contract by where it stands on compliance
        label: 'Contracts',
        icon: 'pi pi-file',
        path: '/contracts',
        items: [
            {
                label: 'All',
                icon: 'pi pi-fw pi-file',
                to: '/contracts/all'
            },
            {
                label: 'Compliant',
                icon: 'pi pi-fw pi-check-circle',
                to: '/contracts/compliant'
            },
            {
                label: 'Pending',
                icon: 'pi pi-fw pi-clock',
                to: '/contracts/pending'
            },
            {
                label: 'Not compliant',
                icon: 'pi pi-fw pi-exclamation-triangle',
                to: '/contracts/not-compliant'
            }
        ]
    },
    {
        // what customers and Zeres say to each other
        label: 'Communication',
        icon: 'pi pi-comments',
        items: [
            {
                label: 'Calls',
                icon: 'pi pi-fw pi-phone',
                to: '/communication/calls'
            }
        ]
    },
    {
        // the catalogue the contracts point at: charger models and the affiliates that refer customers
        label: 'Reference data',
        icon: 'pi pi-database',
        items: [
            {
                label: 'Charger definitions',
                icon: 'pi pi-fw pi-bolt',
                to: '/reference-data/charger-definitions'
            },
            {
                label: 'Affiliates',
                icon: 'pi pi-fw pi-share-alt',
                to: '/reference-data/affiliates'
            }
        ]
    },
    {
        // the settlement chain in the order units move: register, sell, pay out
        label: 'Settlement',
        icon: 'pi pi-sync',
        items: [
            {
                label: 'Registrations',
                icon: 'pi pi-fw pi-check-square',
                to: '/settlement/registrations'
            },
            {
                label: 'Transactions',
                icon: 'pi pi-fw pi-arrow-right-arrow-left',
                to: '/settlement/transactions'
            },
            {
                label: 'Payouts',
                icon: 'pi pi-fw pi-euro',
                to: '/settlement/payouts'
            }
        ]
    },
    {
        // the bookkeeping that records the money after settlement: one entry per ledger
        label: 'Accounting',
        icon: 'pi pi-book',
        items: ledgers.value.map((ledger) => ({
            label: ledger.name,
            icon: 'pi pi-fw pi-book',
            to: `/accounting/ledgers/${ledger.id}`
        }))
    },
    {
        // a group like the others: the vertical sidebar hides a root entry's own link, so a root
        // entry without items would show nothing
        label: 'Settings',
        icon: 'pi pi-cog',
        items: [
            {
                label: 'Service provider',
                icon: 'pi pi-fw pi-building',
                to: '/settings/service-provider'
            },
            {
                label: 'Conversion factors',
                icon: 'pi pi-fw pi-calculator',
                to: '/settings/conversion-factors'
            }
        ]
    }
]);
</script>
